import React, { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { useParams } from "react-router";
import { SplitButton } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { icon, number } from "../../../config";
import { useWorkflowContext } from "../WorkflowContextProvider";
import SearchVariables from "./SearchVariables";
import { useVariablesContext } from "./context/variableContext";
import { useSelectedVariableValues } from "./hooks/workflowVariables.hooks";
import { getWorkflowVariables } from "./services/workflowVariables.service";
import { VARIABLE_ICONS, WORKFLOW_VARIABLE_LABELS, normalizeMultiDimensionType } from "./workflowVariables.constants";

const CustomWorkflowVariables = ({ isFormVariable = false, usedWorkflowVariableIds = [], workflowIdParam, projectId: projectIdProp, searchTerm = "" }) => {
  const {
    customWorkflowVariables,
    setCustomWorkflowVariables,
    toggleVariableList,
    setToggleVariableList,
    setIsNewWorkflowVariable,
    selectedVariable,
    setSelectedVariable,
    showPresetPopup,
    setShowPresetPopup,
    setVariableType,
    formId,
  } = useWorkflowContext();
  const { setSelectedVariableValues } = useSelectedVariableValues();
  const { setHasVarChanges } = useVariablesContext();
  const { workflowId, projectId: projectIdFromRoute } = useParams();
  const projectIdForMerge = projectIdProp ?? projectIdFromRoute;
  const popupSettings = {
    anchorAlign: { vertical: "bottom", horizontal: "right" },
    popupAlign: { vertical: "top", horizontal: "right" },
  };
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [multiDimExpandedById, setMultiDimExpandedById] = useState({});

  /**
   * Loads workflow variables from the API and syncs list state (CustomWorkflowVariables).
   * @author Abhishek Dhillon
   */
  const loadWorkflowVariables = useCallback(async () => {
    if (workflowId || workflowIdParam) {
      const params = { workflowId: workflowId || workflowIdParam };
      if (isFormVariable && formId) {
        params.formId = formId;
      }
      if (isFormVariable && formId && projectIdForMerge) {
        params.projectId = projectIdForMerge;
      }
      const customVariables = await getWorkflowVariables(params);
      setCustomWorkflowVariables(customVariables);
      setFilteredVariables(customVariables);
      setShowPresetPopup(false);
    }
  }, [workflowId, workflowIdParam, formId, isFormVariable, projectIdForMerge]);

  useEffect(() => {
    void loadWorkflowVariables();
  }, [loadWorkflowVariables]);

  // Filter variables based on searchTerm
  useEffect(() => {
    if (customWorkflowVariables?.length > 0) {
      if (searchTerm) {
        const filtered = customWorkflowVariables.filter((variable) => variable.name?.toLowerCase().includes(searchTerm.toLowerCase()));
        setFilteredVariables(filtered);
      } else if (isFormVariable) {
        setFilteredVariables(customWorkflowVariables);
      }
    }
  }, [customWorkflowVariables, searchTerm, isFormVariable]);

  const variableItemClickHandler = async (workflowVariable) => {
    await setIsNewWorkflowVariable((prev) => false);
    setSelectedVariable(workflowVariable);
    setSelectedVariableValues(workflowVariable, workflowVariable?.name);
    setHasVarChanges(false);
  };

  /**
   * Converts variable type to icon string for form builder
   */
  const getVariableIconString = (type) => {
    const iconMap = {
      dropdown: "📋",
      text: "📝",
      number: "🔢",
      date: "📅",
    };
    return iconMap[type] || "📄";
  };

  /**
   * Handles drag start for form variables
   * Converts workflow variable to form component format
   */
  const handleDragStart = (event, variable) => {
    if (isFormVariable) {
      if (variable?.attributes?.multiDimension) {
        flushSync(() => {
          setMultiDimExpandedById((prev) => ({ ...prev, [variable.id]: false }));
        });
      }
      // Convert workflow variable to form component format
      const formComponent = {
        id: `workflow-variable-${variable.type}`,
        name: variable.name,
        type: variable.type,
        icon: getVariableIconString(variable.type), // Convert React icon to string
        workflowVariable: variable, // Keep reference to original variable
        attributes: variable.attributes,
        description: `Workflow ${variable.type} field`,
        chipType: "smart chip",
        chipId: variable.id,
      };

      event.dataTransfer.setData("component", JSON.stringify(formComponent));
      event.dataTransfer.effectAllowed = "copy";
    }
  };

  const handleDimensionDragStart = (event, variable, dimension) => {
    if (!isFormVariable) return;
    event.stopPropagation();
    const formComponent = {
      isWorkflowVariableDimension: true,
      workflowVariable: variable,
      dimension,
      chipType: variable.chipType,
    };
    event.dataTransfer.setData("component", JSON.stringify(formComponent));
    event.dataTransfer.effectAllowed = "copy";
  };

  const addPresetFieldButtonHandler = () => {
    setIsNewWorkflowVariable(true);
    setShowPresetPopup(false);
    setToggleVariableList(!toggleVariableList);
    setSelectedVariable(null);
    setVariableType(WORKFLOW_VARIABLE_LABELS.PREFETCHED_FIELDS);
  };

  const addNewFieldButtonHandler = () => {
    setIsNewWorkflowVariable(true);
    setToggleVariableList(!toggleVariableList);
    setSelectedVariable(null);
    setVariableType(WORKFLOW_VARIABLE_LABELS.CUSTOM_CHIPS);
  };

  /**
   * item's click handler
   * @author Sarthak Arora
   * @param {*} event
   */
  const handleItemClick = (event) => {
    const { item } = event;
    if (item.action) {
      item.action();
    }
  };

  const toggleMultiDimExpanded = (e, variable) => {
    e.stopPropagation();
    setMultiDimExpandedById((prev) => {
      const current = prev[variable.id] === true;
      return { ...prev, [variable.id]: !current };
    });
  };

  const dimTypeIcon = (dimType) => VARIABLE_ICONS[normalizeMultiDimensionType(dimType)] || VARIABLE_ICONS.text;

  /**
   * Sorts workflow variables by type priority and then alphabetically
   * Order: String-based dropdown → Numeric dropdown → Text → Number → Date
   * @author Abhishek Dhillon
   * @param {Array} variables - Array of workflow variables to sort
   * @returns {Array} Sorted array of variables
   */
  const sortWorkflowVariables = (variables) => {
    if (!variables) return [];

    /**
     * Gets the sort priority for a variable
     * Dropdowns are sorted by numberOnly attribute (string-based first, then numeric)
     */
    const getVariablePriority = (variable) => {
      const type = variable?.type;
      if (type === "dropdown") {
        // String-based dropdown (numberOnly: false/undefined) → 0
        // Numeric dropdown (numberOnly: true) → 1
        return variable?.attributes?.numberOnly ? number.ONE : number.ZERO;
      }
      // Other types: text → 2, number → 3, date → 4
      const typePriority = {
        text: number.TWO,
        number: number.THREE,
        date: number.FOUR,
      };
      return typePriority[type] ?? Number.MAX_SAFE_INTEGER;
    };

    return [...variables].sort((a, b) => {
      const priorityA = getVariablePriority(a);
      const priorityB = getVariablePriority(b);
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }
      return (a?.name || "").localeCompare(b?.name || "");
    });
  };
  return (
    <div>
      <div className={`mt-1 ${isFormVariable ? "" : "mb-3"}`}>
        {/* Hide SearchVariables when isFormVariable is true (top-level search is used instead) */}
        {!isFormVariable && (
          <div className='d-flex'>
            <SearchVariables searchItems={customWorkflowVariables} setSearchItems={setFilteredVariables} />
          </div>
        )}
        <div>
          {!toggleVariableList && showPresetPopup && (
            <button className='btn preset-btn mr-1' onClick={addPresetFieldButtonHandler}>
              <span>{WORKFLOW_VARIABLE_LABELS.ADD_PRESET_FIELD}</span>
            </button>
          )}
        </div>
      </div>
      <div
        className={`overflow-y-auto ${isFormVariable ? "custom-form-variable-list-section" : "custom-variable-list-section"} overflow-x-hidden pr-2`}>
        {sortWorkflowVariables(filteredVariables)?.length > number.ZERO ? (
          sortWorkflowVariables(filteredVariables).map((variable) => {
            const isVariableUsed = usedWorkflowVariableIds.includes(variable?.id);
            const isDisabled = isFormVariable && isVariableUsed;
            const isMultiDimension = Boolean(variable?.attributes?.multiDimension);
            const dimensionRows = isMultiDimension ? variable?.attributes?.dimensions ?? [] : [];
            const isMultiDimExpanded = multiDimExpandedById[variable?.id] === true;
            return (
              <>
                <Tooltip anchorElement='target' parentTitle={true} position='bottom'>
                  <div
                    key={variable?.id}
                    className={`d-flex p-2 workflow-variable-item w-100 ${variable?.id === selectedVariable?.id ? "selected-variable" : ""} ${
                      isFormVariable ? (isDisabled ? "disabled-variable cursor-pointer" : "draggable-variable cursor-grab") : "cursor-pointer"
                    } ${isDisabled ? "disabled-variable" : ""}`}
                    onClick={() => variableItemClickHandler(variable)}
                    draggable={isFormVariable && !isDisabled}
                    onDragStart={(e) => !isDisabled && handleDragStart(e, variable)}>
                    <div className='d-flex flex-column w-100'>
                      <div className='d-flex w-100 variable-row-gap align-items-start'>
                        <span className='variable-icon  flex-shrink-0'>
                          {variable?.type === WORKFLOW_VARIABLE_LABELS.DROPDOWN
                            ? variable?.attributes?.numberOnly
                              ? VARIABLE_ICONS.number_dropdown
                              : VARIABLE_ICONS.dropdown
                            : VARIABLE_ICONS[variable?.type]
                            ? VARIABLE_ICONS[variable?.type]
                            : VARIABLE_ICONS.dropdown}
                        </span>
                        <div className='variable-name-container text-truncate'>
                          <span className={`${isFormVariable ? "" : ""} variable-name`}>{variable?.name}</span>
                        </div>
                        <div className='d-flex align-items-center flex-shrink-0'>
                          {variable?.adminType === WORKFLOW_VARIABLE_LABELS.IS_ADMIN_FIELD && (
                            <div title='Global' className='custom-variable smart-chip-lock mr-2'>
                              {icon.LOCK_ICON}
                            </div>
                          )}
                          {isMultiDimension && dimensionRows.length > 0 && (
                            <div className='d-flex ml-2'>
                              {isMultiDimension && (
                                <span
                                  className='multi-dimension-chip-icon flex-shrink-0 d-inline-flex align-items-center'
                                  title={WORKFLOW_VARIABLE_LABELS.MULTI_DIMENSION}>
                                  {icon.MULTIDIMENSION}
                                </span>
                              )}
                              <button
                                type='button'
                                className='multi-dimension-chevron-btn'
                                aria-expanded={isMultiDimExpanded}
                                onClick={(e) => toggleMultiDimExpanded(e, variable)}>
                                {isMultiDimExpanded ? icon.CHEVRON_UP : icon.CHEVRON_DOWN}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      {isMultiDimension && isMultiDimExpanded && dimensionRows.length > 0 && (
                        <div className='multi-dimension-dimensions-list'>
                          {dimensionRows.map((dim) => {
                            const dimDraggable = isFormVariable && isVariableUsed;
                            return (
                              <div
                                key={dim.id}
                                className={`multi-dimension-dimension-row d-flex align-items-center gap-2 ${
                                  dimDraggable ? "draggable-variable cursor-grab" : ""
                                }`}
                                draggable={dimDraggable}
                                onDragStart={(e) => dimDraggable && handleDimensionDragStart(e, variable, dim)}
                                onClick={(e) => isFormVariable && e.stopPropagation()}>
                                <span className='flex-shrink-0 d-inline-flex align-items-center'>{dimTypeIcon(dim.type)}</span>
                                <span className='text-truncate small text-body'>{dim.label ?? ""}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {(!!variable?.variableId?.description || !!variable?.description) && (
                        <div className='w-100 variable-description-container mt-1'>
                          <span className=' text-nowrap d-block variable-description-text text-truncate'>
                            {variable?.description || variable?.variableId?.description}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Tooltip>
              </>
            );
          })
        ) : searchTerm ? (
          <div className='text-muted text-center p-3'>No variables found</div>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
};

export default CustomWorkflowVariables;
